import {
  REPORT_REASONS,
  type AdminDay,
  type AdminOverview,
  type AdminQuestion,
  type ContentStatus,
  type PoolOrigin,
  type ReportReason,
  type SpecialInfo,
  SPORTS,
} from '@geosport/shared';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Button } from '../components/Button';
import { Shell } from '../components/Shell';
import { SportIcon } from '../components/SportIcon';
import { getPlayerToken } from '../lib/api';
import { readString, STORAGE_KEYS, writeString } from '../lib/storage';

const BASE = import.meta.env.VITE_API_BASE ?? '';

class AdminError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

/** Admin calls carry the editor's token; answers come back, so nothing is ever cached. */
async function adminFetch<T>(token: string, path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}/api/admin${path}`, {
      ...init,
      cache: 'no-store',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(init.headers as Record<string, string> | undefined),
      },
    });
  } catch {
    throw new AdminError('You appear to be offline.', 0);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new AdminError(body?.error ?? `Request failed (${res.status})`, res.status);
  }
  return (await res.json()) as T;
}

const forgetToken = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.admin);
  } catch {
    /* storage unavailable */
  }
};

const prettyDay = (date: string): string =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

const ORIGIN_LABEL: Record<PoolOrigin | 'recycled' | 'imported', string> = {
  ai: 'AI · checked',
  editor: 'Editor',
  sheet: 'Sheet',
  recycled: 'Recycled',
  imported: 'Imported',
};

/** The editor's view of the question bank: what's coming, the pool, real scores, vetoes. */
export function Admin() {
  const [token, setToken] = useState<string | null>(() => readString(STORAGE_KEYS.admin));
  const signOut = useCallback((): void => {
    forgetToken();
    setToken(null);
  }, []);

  // Keep this page out of search engines (it is not linked from the game either).
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  return (
    <Shell title="Question bank" wide>
      {token ? (
        <Dashboard token={token} onSignOut={signOut} />
      ) : (
        <Unlock
          onUnlock={(t) => {
            writeString(STORAGE_KEYS.admin, t);
            setToken(t);
          }}
        />
      )}
    </Shell>
  );
}

function Unlock({ onUnlock }: { onUnlock: (token: string) => void }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const candidate = value.trim();
    if (!candidate) return;
    setBusy(true);
    setError(null);
    try {
      await adminFetch<ContentStatus>(candidate, '/status');
      onUnlock(candidate);
    } catch (err) {
      setError(
        err instanceof AdminError && err.status === 401
          ? 'That token didn’t work.'
          : err instanceof Error
            ? err.message
            : 'Something went wrong.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="grid max-w-sm gap-3">
      <p className="text-sm text-white/70">
        Editors only. Paste the admin token to see upcoming questions with their answers.
      </p>
      <label className="grid gap-1 text-sm">
        <span className="text-white/70">Admin token</span>
        <input
          type="password"
          autoComplete="off"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="min-h-11 rounded-xl bg-white/10 px-3 text-white ring-1 ring-white/15 outline-none focus:ring-accent"
        />
      </label>
      {error && (
        <p className="text-sm text-red-300" role="alert">
          {error}
        </p>
      )}
      <Button type="submit" disabled={busy || !value.trim()}>
        {busy ? 'Checking…' : 'Unlock'}
      </Button>
    </form>
  );
}

interface PlannedPost {
  date: string;
  slot: string;
  label: string;
  time: string;
  status: 'posted' | 'skipped' | 'pending' | 'missed' | 'nothing to post';
  posts: { platform: 'bsky' | 'x'; text: string; length: number; limit: number }[];
}

const STATUS_STYLE: Record<PlannedPost['status'], string> = {
  posted: 'text-emerald-300',
  skipped: 'text-white/40 line-through',
  pending: 'text-accent',
  missed: 'text-white/40',
  'nothing to post': 'text-white/40',
};

/** Today's and tomorrow's social posts (Bluesky and X), with a Skip button per post. */
function SocialSchedule({ token }: { token: string }) {
  const [planned, setPlanned] = useState<PlannedPost[] | null>(null);
  const [configured, setConfigured] = useState<{ bsky: boolean; x: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let alive = true;
    adminFetch<{ planned: PlannedPost[]; configured: { bsky: boolean; x: boolean } }>(
      token,
      '/social/preview',
    ).then(
      (data) => {
        if (!alive) return;
        setPlanned(data.planned);
        setConfigured(data.configured);
      },
      (err: unknown) => alive && setError(err instanceof Error ? err.message : 'Could not load.'),
    );
    return () => {
      alive = false;
    };
  }, [token, version]);

  const toggle = async (p: PlannedPost): Promise<void> => {
    try {
      await adminFetch(token, '/social/skip', {
        method: 'POST',
        body: JSON.stringify({ slot: p.slot, date: p.date, skip: p.status !== 'skipped' }),
      });
      setVersion((v) => v + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };

  return (
    <details className="rounded-2xl bg-white/5 p-4">
      <summary className="cursor-pointer font-semibold">
        Social posts, today and tomorrow
        {configured && (
          <span className="ml-2 text-xs font-normal text-white/50">
            Bluesky {configured.bsky ? 'on' : 'off'} · X {configured.x ? 'on' : 'off'}
          </span>
        )}
      </summary>
      {error && <p className="mt-2 text-sm text-red-300">{error}</p>}
      {!planned ? (
        <p className="mt-2 text-sm text-white/60">Loading…</p>
      ) : (
        <ul className="mt-3 grid gap-3">
          {planned.map((p) => (
            <li key={`${p.date}-${p.slot}`} className="rounded-xl bg-black/20 p-3 text-sm">
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold">
                  {prettyDay(p.date)} {p.time} · {p.label}
                </span>
                <span className="flex items-center gap-2">
                  <span className={`text-xs ${STATUS_STYLE[p.status]}`}>{p.status}</span>
                  {(p.status === 'pending' || p.status === 'skipped') && (
                    <Button variant="ghost" size="sm" onClick={() => void toggle(p)}>
                      {p.status === 'skipped' ? 'Undo skip' : 'Skip'}
                    </Button>
                  )}
                </span>
              </div>
              {p.posts[0] && (
                <pre className="whitespace-pre-wrap font-sans text-white/80">{p.posts[0].text}</pre>
              )}
              {p.posts.length > 0 && (
                <p className="mt-1 text-xs text-white/40">
                  {p.posts
                    .map((x) => `${x.platform === 'x' ? 'X' : 'Bluesky'} ${x.length}/${x.limit}`)
                    .join(' · ')}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}

interface MakerInfo {
  name: string;
  id: string;
}

/** "Beat the maker": players see this player's score on the same puzzle after they finish. */
function MakerPanel({ token }: { token: string }) {
  const [maker, setMaker] = useState<MakerInfo | null | undefined>(undefined);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    adminFetch<{ maker: MakerInfo | null }>(token, '/maker').then(
      (d) => {
        if (!alive) return;
        setMaker(d.maker);
        setName(d.maker?.name ?? '');
      },
      (err: unknown) => alive && setError(err instanceof Error ? err.message : 'Could not load.'),
    );
    return () => {
      alive = false;
    };
  }, [token]);

  const save = async (clear: boolean): Promise<void> => {
    setError(null);
    try {
      const player = getPlayerToken();
      const d = await adminFetch<{ maker: MakerInfo | null }>(token, '/maker', {
        method: 'POST',
        body: JSON.stringify(clear ? { clear: true } : { name }),
        headers: player ? { 'X-Player': player } : {},
      });
      setMaker(d.maker);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };

  return (
    <details className="rounded-2xl bg-white/5 p-4">
      <summary className="cursor-pointer font-semibold">
        Beat the maker
        <span className="ml-2 text-xs font-normal text-white/50">
          {maker === undefined ? '' : maker ? `${maker.name} (${maker.id}…)` : 'off'}
        </span>
      </summary>
      <p className="mt-2 text-sm text-white/60">
        After finishing, players see the maker’s score on the same puzzle. Play the daily round in
        this browser, then press the button: this browser’s player becomes the maker.
      </p>
      {error && <p className="mt-2 text-sm text-red-300">{error}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={30}
          placeholder="Name shown to players"
          className="min-w-0 flex-1 rounded-xl bg-black/30 px-3 py-2 text-sm ring-1 ring-white/10"
        />
        <Button size="sm" onClick={() => void save(false)}>
          Use this browser
        </Button>
        {maker && (
          <Button variant="ghost" size="sm" onClick={() => void save(true)}>
            Turn off
          </Button>
        )}
      </div>
    </details>
  );
}

interface AdminSpecial extends SpecialInfo {
  pitch: { subject: string; body: string };
}

const inputClass = 'min-w-0 rounded-xl bg-black/30 px-3 py-2 text-sm ring-1 ring-white/10';

/** Themed specials: create from unused pool questions, copy the press pitch, delete. */
function SpecialsPanel({ token }: { token: string }) {
  const [list, setList] = useState<AdminSpecial[] | null>(null);
  const [unused, setUnused] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [form, setForm] = useState({
    slug: '',
    title: '',
    blurb: '',
    sport: '',
    keywords: '',
    starts: '',
    ends: '',
  });

  useEffect(() => {
    let alive = true;
    adminFetch<{ specials: AdminSpecial[]; unused: Record<string, number> }>(
      token,
      '/specials',
    ).then(
      (d) => {
        if (!alive) return;
        setList(d.specials);
        setUnused(d.unused);
      },
      (err: unknown) => alive && setError(err instanceof Error ? err.message : 'Could not load.'),
    );
    return () => {
      alive = false;
    };
  }, [token, version]);

  const field =
    (k: keyof typeof form) =>
    (e: { target: { value: string } }): void =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    try {
      const d = await adminFetch<{ questions: { clue: string; answer: string; year: number }[] }>(
        token,
        '/specials',
        { method: 'POST', body: JSON.stringify(form) },
      );
      setNotice(`Created with: ${d.questions.map((q) => `${q.answer} ${q.year}`).join(' · ')}`);
      setForm({ slug: '', title: '', blurb: '', sport: '', keywords: '', starts: '', ends: '' });
      setVersion((v) => v + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };

  const remove = async (slug: string): Promise<void> => {
    if (!window.confirm(`Delete the special “${slug}”?`)) return;
    try {
      await adminFetch(token, `/specials/${slug}`, { method: 'DELETE' });
      setVersion((v) => v + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };

  const copy = async (p: AdminSpecial['pitch']): Promise<void> => {
    try {
      await navigator.clipboard.writeText(`Subject: ${p.subject}\n\n${p.body}`);
      setNotice('Pitch copied ✓');
    } catch {
      setError('Could not copy.');
    }
  };

  return (
    <details className="rounded-2xl bg-white/5 p-4">
      <summary className="cursor-pointer font-semibold">
        Specials
        <span className="ml-2 text-xs font-normal text-white/50">
          {list ? `${list.length} made` : ''}
        </span>
      </summary>
      <p className="mt-2 text-sm text-white/60">
        Five unused questions from the pool, easiest first. Playable from the start date at
        /specials/slug; posted to Bluesky and X at 10:00 on that day. Unused now:{' '}
        {SPORTS.map((sp) => `${sp} ${unused[sp] ?? 0}`).join(' · ')}
      </p>
      {error && <p className="mt-2 text-sm text-red-300">{error}</p>}
      {notice && <p className="mt-2 text-sm text-emerald-300">{notice}</p>}
      <form onSubmit={(e) => void create(e)} className="mt-3 grid gap-2 sm:grid-cols-2">
        <input
          className={inputClass}
          placeholder="slug, e.g. ashes-2026"
          value={form.slug}
          onChange={field('slug')}
          required
        />
        <input
          className={inputClass}
          placeholder="Title, e.g. The Ashes special"
          value={form.title}
          onChange={field('title')}
          maxLength={60}
          required
        />
        <input
          className={`${inputClass} sm:col-span-2`}
          placeholder="One-line blurb shown on the page and in posts"
          value={form.blurb}
          onChange={field('blurb')}
          maxLength={240}
          required
        />
        <select className={inputClass} value={form.sport} onChange={field('sport')}>
          <option value="">Any sport</option>
          {SPORTS.map((sp) => (
            <option key={sp} value={sp}>
              {sp}
            </option>
          ))}
        </select>
        <input
          className={inputClass}
          placeholder="Keywords (any), e.g. Ashes Lord's"
          value={form.keywords}
          onChange={field('keywords')}
        />
        <label className="grid gap-1 text-xs text-white/60">
          Starts
          <input
            type="date"
            className={inputClass}
            value={form.starts}
            onChange={field('starts')}
          />
        </label>
        <label className="grid gap-1 text-xs text-white/60">
          Ends
          <input type="date" className={inputClass} value={form.ends} onChange={field('ends')} />
        </label>
        <Button type="submit" size="sm" className="sm:col-span-2">
          Create special
        </Button>
      </form>
      {list && list.length > 0 && (
        <ul className="mt-4 grid gap-3">
          {list.map((sp) => (
            <li key={sp.slug} className="rounded-xl bg-black/20 p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <a href={`/specials/${sp.slug}`} className="font-semibold text-accent">
                  {sp.title}
                </a>
                <span className="text-xs text-white/50">
                  {prettyDay(sp.starts)} – {prettyDay(sp.ends)}
                </span>
              </div>
              <p className="mt-1 text-white/70">{sp.blurb}</p>
              <div className="mt-2 flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => void copy(sp.pitch)}>
                  Copy press pitch
                </Button>
                <Button variant="ghost" size="sm" onClick={() => void remove(sp.slug)}>
                  Delete
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}

function Dashboard({ token, onSignOut }: { token: string; onSignOut: () => void }) {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Bumped to reload the overview after an action or on "Refresh".
  const [version, setVersion] = useState(0);
  const reload = (): void => setVersion((v) => v + 1);

  useEffect(() => {
    let alive = true;
    adminFetch<AdminOverview>(token, '/overview').then(
      (data) => {
        if (!alive) return;
        setOverview(data);
        setError(null);
      },
      (err: unknown) => {
        if (!alive) return;
        if (err instanceof AdminError && err.status === 401) onSignOut();
        else setError(err instanceof Error ? err.message : 'Could not load the overview.');
      },
    );
    return () => {
      alive = false;
    };
  }, [token, onSignOut, version]);

  const act = async (path: string, body: unknown, done: string): Promise<void> => {
    try {
      await adminFetch(token, path, { method: 'POST', body: JSON.stringify(body) });
      setNotice(done);
      reload();
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };

  if (error) {
    return (
      <div className="grid gap-3">
        <p className="text-red-300">{error}</p>
        <Button variant="secondary" onClick={reload}>
          Try again
        </Button>
      </div>
    );
  }
  if (!overview) return <p className="text-white/60">Loading the question bank…</p>;

  const { status } = overview;
  const today = status.today;
  const upcoming = overview.days.filter((d) => d.date >= today);
  const past = overview.days.filter((d) => d.date < today).reverse();
  const low = status.runwayDays < status.horizonDays;

  return (
    <div className="grid gap-8">
      <section className="grid gap-3">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Days scheduled" value={status.scheduledDays} />
          <Stat label="Ready in pool" value={status.pool.ready} />
          <Stat label="Runway (days)" value={status.runwayDays} warn={low} />
          <Stat label="Rejected" value={status.pool.rejected} />
        </div>
        <p className="text-sm text-white/60">
          The next {status.horizonDays} days are filled from the pool automatically; reject anything
          before its day and the slot is refilled. When the pool runs dry, older questions are
          recycled so a day is never empty.
          {low && (
            <span className="text-accent">
              {' '}
              The runway is short: check that the daily generator job is running.
            </span>
          )}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={reload}>
            Refresh
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void act('/schedule', {}, 'Schedule refreshed.')}
          >
            Fill schedule now
          </Button>
          <Button variant="ghost" size="sm" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
        {notice && (
          <p role="status" className="text-sm text-emerald-300">
            {notice}
          </p>
        )}
      </section>

      <SocialSchedule token={token} />
      <MakerPanel token={token} />
      <SpecialsPanel token={token} />

      {overview.reports.length > 0 && (
        <section className="grid gap-3">
          <h2 className="text-xl font-bold">
            Player reports, last 30 days ({overview.reports.length})
          </h2>
          <ul className="grid gap-3">
            {overview.reports.map((r) => (
              <li key={r.questionId} className="rounded-2xl bg-white/5 p-4 text-sm">
                <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-semibold">
                    {prettyDay(r.date)} · Q{r.position} · {r.answerPlace}, {r.year}
                  </span>
                  <span className="text-accent font-bold">
                    {r.count} report{r.count === 1 ? '' : 's'}
                  </span>
                </div>
                <p className="text-white/80">{r.clue}</p>
                <p className="mt-1 text-white/60">
                  {Object.entries(r.reasons)
                    .map(([k, n]) => `${REPORT_REASONS[k as ReportReason]} ×${n}`)
                    .join(' · ')}
                </p>
                {r.notes.length > 0 && (
                  <ul className="mt-2 list-disc pl-5 text-white/70">
                    {r.notes.map((n, i) => (
                      <li key={i}>{n}</li>
                    ))}
                  </ul>
                )}
                <a
                  className="mt-2 inline-block text-white/60 underline"
                  href={r.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Source
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="grid gap-4">
        <h2 className="text-xl font-bold">Coming up</h2>
        {upcoming.map((day) => (
          <DayCard
            key={day.date}
            day={day}
            today={today}
            onReject={(q, reason) =>
              void act(
                `/questions/${q.id}/reject`,
                { reason },
                `Rejected; ${prettyDay(day.date)} refilled.`,
              )
            }
          />
        ))}
      </section>

      <section className="grid gap-3">
        <h2 className="text-xl font-bold">In the pool, next in line ({overview.pool.length})</h2>
        {overview.pool.length === 0 ? (
          <p className="text-sm text-white/60">Empty. The generator tops it up daily.</p>
        ) : (
          <ul className="grid gap-3">
            {overview.pool.map((q) => (
              <QuestionCard
                key={q.id}
                q={q}
                onReject={(reason) =>
                  void act(`/pool/${q.id}/reject`, { reason }, 'Removed from the pool.')
                }
              />
            ))}
          </ul>
        )}
      </section>

      <section className="grid gap-4">
        <h2 className="text-xl font-bold">Last 7 days</h2>
        {past.map((day) => (
          <DayCard key={day.date} day={day} today={today} />
        ))}
      </section>

      {overview.rejected.length > 0 && (
        <details className="rounded-2xl bg-white/5 p-4">
          <summary className="cursor-pointer font-semibold">
            Recently rejected ({overview.rejected.length})
          </summary>
          <ul className="mt-3 grid gap-3">
            {overview.rejected.map((q) => (
              <QuestionCard key={q.id} q={q} />
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Stat({ label, value, warn = false }: { label: string; value: number; warn?: boolean }) {
  return (
    <div
      className={`rounded-2xl p-3 ${warn ? 'bg-accent/15 ring-1 ring-accent/40' : 'bg-white/5'}`}
    >
      <div className="text-xs text-white/60">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function DayCard({
  day,
  today,
  onReject,
}: {
  day: AdminDay;
  today: string;
  onReject?: (q: AdminQuestion, reason: string) => void;
}) {
  const editable = day.date > today && onReject;
  return (
    <div className="rounded-2xl bg-ink-2 p-3 ring-1 ring-white/10">
      <h3 className="mb-2 flex items-baseline gap-2 font-semibold">
        {prettyDay(day.date)}
        <span className="text-sm font-normal text-white/50">#{day.puzzleNo}</span>
        {day.date === today && (
          <span className="rounded-full bg-accent px-2 text-xs font-bold text-ink">today</span>
        )}
      </h3>
      {day.questions.length === 0 ? (
        <p className="text-sm text-white/60">
          Nothing scheduled yet: the pool fills it, or older questions are recycled on the day.
        </p>
      ) : (
        <ul className="grid gap-2">
          {day.questions.map((q) => (
            <QuestionCard
              key={q.id}
              q={q}
              onReject={editable ? (reason) => onReject(q, reason) : undefined}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function QuestionCard({ q, onReject }: { q: AdminQuestion; onReject?: (reason: string) => void }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  return (
    <li className="rounded-xl bg-white/5 p-3">
      <div className="flex items-start gap-2">
        {q.position !== null && (
          <span className="mt-0.5 rounded-md bg-white/10 px-1.5 text-xs font-bold tabular-nums">
            Q{q.position}
          </span>
        )}
        <SportIcon sport={q.sport} className="text-lg leading-6" />
        <p className="font-semibold">{q.clue}</p>
      </div>
      <p className="mt-1 text-sm text-accent">
        {q.answerPlace} · {q.year}
      </p>
      <p className="mt-1 text-sm text-white/65">{q.story}</p>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/55">
        <span>{ORIGIN_LABEL[q.origin]}</span>
        <span>Difficulty {q.difficulty}/5</span>
        {q.avgBase !== null && (
          <span>
            Players’ average {Math.round(q.avgBase)}/100 ({q.plays} played)
          </span>
        )}
        <a
          href={q.sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-2 hover:text-white"
        >
          Source
        </a>
        {q.notes && <span className="text-white/45">{q.notes}</span>}
      </div>
      {onReject &&
        (rejecting ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional)"
              aria-label="Reason for rejecting"
              className="min-h-10 flex-1 rounded-xl bg-white/10 px-3 text-sm ring-1 ring-white/15 outline-none focus:ring-accent"
            />
            <Button
              size="sm"
              className="bg-red-500 text-white hover:bg-red-400"
              onClick={() => {
                setRejecting(false);
                onReject(reason.trim());
              }}
            >
              Reject
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="ghost" className="mt-2" onClick={() => setRejecting(true)}>
            Reject…
          </Button>
        ))}
    </li>
  );
}
